import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Download, ChartBar } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const statusColors = {
  P: { bg: '#dcfce7', color: '#16a34a' },
  A: { bg: '#fee2e2', color: '#dc2626' },
  L: { bg: '#fef3c7', color: '#d97706' },
  H: { bg: '#dbeafe', color: '#2563eb' },
  WO: { bg: '#f1f5f9', color: '#64748b' },
};

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthBack = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const AttendanceReportPage = () => {
  const [fromMonth, setFromMonth] = useState(monthBack(2));
  const [toMonth, setToMonth] = useState(today());
  const [employees, setEmployees] = useState([]);
  const [empFilter, setEmpFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('employee'); // 'employee' | 'month' | 'date'
  const [dateMonth, setDateMonth] = useState(today());
  const [dateData, setDateData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/employees`, { withCredentials: true })
      .then((r) => setEmployees(r.data || []))
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from_month: fromMonth, to_month: toMonth };
      if (empFilter) params.employee_id = empFilter;
      const r = await axios.get(`${API_URL}/api/attendance-report`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromMonth, toMonth, empFilter]);

  useEffect(() => {
    if (groupBy !== 'date') return;
    let cancelled = false;
    axios.get(`${API_URL}/api/attendance-report/date-wise`, { params: { month_year: dateMonth }, withCredentials: true })
      .then((r) => { if (!cancelled) setDateData(r.data); })
      .catch(() => { if (!cancelled) setDateData(null); });
    return () => { cancelled = true; };
  }, [groupBy, dateMonth]);

  const exportDateCSV = () => {
    if (!dateData) return;
    const header = ['Date', 'Day', ...dateData.employees.map((e) => `${e.name} (${e.emp_id || ''})`)];
    const lines = [header.join(',')];
    dateData.dates.forEach((d) => {
      const row = [d.date, d.weekday, ...dateData.employees.map((e) => d.cells[e.employee_id] || '')];
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `attendance_datewise_${dateMonth}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Build month-wise summary (for groupBy='month')
  const monthlyTotals = useMemo(() => {
    if (!data) return [];
    return data.months.map((my) => {
      const totals = { P: 0, A: 0, L: 0, H: 0, WO: 0 };
      data.employees.forEach((e) => {
        const c = e.months[my] || {};
        ['P', 'A', 'L', 'H', 'WO'].forEach((k) => { totals[k] += (c[k] || 0); });
      });
      return { month: my, ...totals };
    });
  }, [data]);

  const exportCSV = () => {
    if (!data) return;
    const months = data.months;
    let header = ['Emp ID', 'Name'];
    months.forEach((m) => header.push(`${m} P`, `${m} A`, `${m} L`, `${m} H`, `${m} WO`));
    header.push('Total P', 'Total A', 'Total L', 'Total H', 'Total WO');
    const lines = [header.join(',')];
    data.employees.forEach((e) => {
      const row = [e.emp_id || '', `"${(e.name || '').replace(/"/g, '""')}"`];
      months.forEach((m) => {
        const c = e.months[m] || {};
        row.push(c.P || 0, c.A || 0, c.L || 0, c.H || 0, c.WO || 0);
      });
      row.push(e.totals.P || 0, e.totals.A || 0, e.totals.L || 0, e.totals.H || 0, e.totals.WO || 0);
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `attendance_${fromMonth}_to_${toMonth}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="attendance-report-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>
            <ChartBar size={22} className="inline mr-1.5 mb-1" /> Attendance Reports
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            Employee-wise / month-wise / date-range summary
          </p>
        </div>
        <Button variant="outline" onClick={groupBy === 'date' ? exportDateCSV : exportCSV} disabled={groupBy === 'date' ? !dateData : !data} data-testid="export-csv-btn">
          <Download size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {groupBy === 'date' ? (
            <>
              <div>
                <Label className="text-xs">Month</Label>
                <input type="month" value={dateMonth} onChange={(e) => setDateMonth(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-date-month" />
              </div>
              <div className="md:col-span-2" />
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">From Month</Label>
                <input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-from" />
              </div>
              <div>
                <Label className="text-xs">To Month</Label>
                <input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-to" />
              </div>
              <div>
                <Label className="text-xs">Employee</Label>
                <select value={empFilter} onChange={(e) => setEmpFilter(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-emp">
                  <option value="">All employees</option>
                  {employees.map((e) => (<option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>))}
                </select>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">View</Label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-group">
              <option value="employee">By Employee</option>
              <option value="month">By Month (overall)</option>
              <option value="date">By Date (grid)</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--dx-text2)' }}>
        {Object.entries(statusColors).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className="inline-block w-4 h-4 rounded font-bold text-center text-[10px] leading-4" style={{ background: v.bg, color: v.color }}>{k}</span>
            {k === 'P' ? 'Present' : k === 'A' ? 'Absent' : k === 'L' ? 'Leave' : k === 'H' ? 'Holiday' : 'Week Off'}
          </span>
        ))}
      </div>

      {loading ? (
        <Card className="p-8 text-center text-sm" style={{ color: 'var(--dx-text3)' }}>Loading...</Card>
      ) : groupBy === 'date' ? (
        !dateData || dateData.dates.length === 0 ? (
          <Card className="p-8 text-center text-sm" style={{ color: 'var(--dx-text3)' }}>No data for {dateMonth}.</Card>
        ) : (
          <Card className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: 'var(--dx-bg)' }}>
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold sticky left-0 bg-inherit" style={{ background: 'var(--dx-bg)', minWidth: 110 }}>Date</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Day</th>
                  {dateData.employees.map((e) => (
                    <th key={e.employee_id} className="px-2 py-2.5 font-semibold text-center text-[11px]" style={{ minWidth: 70 }}>
                      <div>{e.name}</div>
                      <div className="text-[9px] font-normal" style={{ color: 'var(--dx-text3)' }}>{e.emp_id}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dateData.dates.map((d) => {
                  const isWeekend = d.weekday === 'Sat' || d.weekday === 'Sun';
                  return (
                    <tr key={d.date} className="border-t" style={{ background: isWeekend ? '#fafbfd' : 'white' }}>
                      <td className="px-3 py-2 font-medium sticky left-0" style={{ background: isWeekend ? '#fafbfd' : 'white' }}>
                        {d.date.split('-').reverse().join('/')}
                      </td>
                      <td className="px-3 py-2 text-xs" style={{ color: 'var(--dx-text2)' }}>{d.weekday}</td>
                      {dateData.employees.map((e) => {
                        const st = d.cells[e.employee_id] || '';
                        const _map = { P: 'P', CO: 'P', A: 'A', CL: 'L', LOP: 'L', H: 'H', WO: 'WO' };
                        const bucket = _map[st] || st;
                        const sc = statusColors[bucket] || { bg: '#f3f4f6', color: '#64748b' };
                        return (
                          <td key={`${d.date}-${e.employee_id}`} className="px-2 py-1.5 text-center">
                            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: sc.bg, color: sc.color, minWidth: 28 }}>
                              {st}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )
      ) : !data || data.employees.length === 0 ? (
        <Card className="p-8 text-center text-sm" style={{ color: 'var(--dx-text3)' }}>No data for the selected range.</Card>
      ) : groupBy === 'month' ? (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--dx-bg)' }}>
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Month</th>
                <th className="px-4 py-2.5 font-semibold text-center">P</th>
                <th className="px-4 py-2.5 font-semibold text-center">A</th>
                <th className="px-4 py-2.5 font-semibold text-center">L</th>
                <th className="px-4 py-2.5 font-semibold text-center">H</th>
                <th className="px-4 py-2.5 font-semibold text-center">WO</th>
              </tr>
            </thead>
            <tbody>
              {monthlyTotals.map((m) => {
                const [y, mm] = m.month.split('-');
                return (
                  <tr key={m.month} className="border-t">
                    <td className="px-4 py-2.5 font-medium">{MONTHS[+mm - 1]} {y}</td>
                    <td className="px-4 py-2.5 text-center"><span className="font-semibold" style={{ color: statusColors.P.color }}>{m.P}</span></td>
                    <td className="px-4 py-2.5 text-center"><span className="font-semibold" style={{ color: statusColors.A.color }}>{m.A}</span></td>
                    <td className="px-4 py-2.5 text-center"><span className="font-semibold" style={{ color: statusColors.L.color }}>{m.L}</span></td>
                    <td className="px-4 py-2.5 text-center"><span className="font-semibold" style={{ color: statusColors.H.color }}>{m.H}</span></td>
                    <td className="px-4 py-2.5 text-center"><span className="font-semibold" style={{ color: statusColors.WO.color }}>{m.WO}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--dx-bg)' }}>
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold sticky left-0 bg-inherit" style={{ background: 'var(--dx-bg)' }}>Employee</th>
                {data.months.map((my) => {
                  const [y, mm] = my.split('-');
                  return (<th key={my} className="px-3 py-2.5 font-semibold text-center" colSpan={5}>{MONTHS[+mm - 1]} {y}</th>);
                })}
                <th className="px-3 py-2.5 font-semibold text-center" colSpan={5} style={{ background: '#e0f2fe' }}>TOTAL</th>
              </tr>
              <tr style={{ background: 'var(--dx-bg)' }}>
                <th></th>
                {data.months.flatMap((my) => ['P', 'A', 'L', 'H', 'WO'].map((s) => (
                  <th key={`${my}-${s}`} className="px-1 py-1 text-[10px] font-semibold text-center" style={{ color: statusColors[s].color }}>{s}</th>
                )))}
                {['P', 'A', 'L', 'H', 'WO'].map((s) => (
                  <th key={`total-${s}`} className="px-1 py-1 text-[10px] font-semibold text-center" style={{ background: '#e0f2fe', color: statusColors[s].color }}>{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => (
                <tr key={e.employee_id} className="border-t hover:bg-gray-50" data-testid={`row-${e.employee_id}`}>
                  <td className="px-3 py-2.5 font-medium sticky left-0 bg-white">
                    <div>{e.name}</div>
                    <div className="text-[10px]" style={{ color: 'var(--dx-text3)' }}>{e.emp_id}</div>
                  </td>
                  {data.months.flatMap((my) => {
                    const c = e.months[my] || {};
                    return ['P', 'A', 'L', 'H', 'WO'].map((s) => (
                      <td key={`${e.employee_id}-${my}-${s}`} className="px-1 py-1 text-center text-xs">
                        {c[s] || 0}
                      </td>
                    ));
                  })}
                  {['P', 'A', 'L', 'H', 'WO'].map((s) => (
                    <td key={`tot-${e.employee_id}-${s}`} className="px-1 py-1 text-center text-xs font-bold" style={{ background: '#e0f2fe', color: statusColors[s].color }}>
                      {e.totals[s] || 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default AttendanceReportPage;
